import React, { useCallback, useMemo, useState } from 'react';
import { Tag, Select, Button, Spin, message } from 'antd';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { formatPriceWithNoSymbol } from '~helpers/currency';
import { useQuery, useMutation } from '@apollo/client';
import { QUERY_COMMISSION_PLANS } from '~pages/dashboard/referral/commission-plan/queries';
import {
  MUTATION_MODIFY_PARTNER_COMMISSION_PLAN,
  QUERY_PARTNER_CONTACT,
} from '~pages/dashboard/referral/partner/queries';

function PartnerDetailCommissionPlanEditor({
  partnerId,
  partnerCountryCode,
  currentCommissionPlanId,
  onClose,
}) {
  const { t } = useTranslation();

  const [selectedCommissionId, setSelectCommissionId] = useState(currentCommissionPlanId);

  const { loading: commissionPlanListLoading, data: commissionPlanListResponse } = useQuery(
    QUERY_COMMISSION_PLANS,
    {
      variables: {
        pageNumber: 1,
        pageSize: 100,
        countryCode: partnerCountryCode,
        sortBy: [
          { sortField: 'DEFAULT', sortDirection: 'DESCENDING' },
          { sortField: 'UPDATED_AT', sortDirection: 'DESCENDING' },
        ],
      },
      onError: e => {
        message.error(e?.toString());
      },
    },
  );

  const [updatePartnerCommissionPlan, { loading: updatePartnerCommissionPlanLoading }] =
    useMutation(MUTATION_MODIFY_PARTNER_COMMISSION_PLAN, {
      refetchQueries: [QUERY_PARTNER_CONTACT],
      onError: e => {
        message.error(e.message);
      },
    });

  const handleSubmit = useCallback(async (partnerId, selectedCommissionPlanId) => {
    const response = await updatePartnerCommissionPlan({
      variables: { partnerId, commissionPlanId: selectedCommissionPlanId },
    });
    if (response?.data?.modifyPartnerCommissionPlanId?.result) {
      message.success(
        t('cms.referral.partner.detail.editor.action.update_commission.toast.success'),
      );
      onClose();
    }
  }, []);

  const commissionPlanList = useMemo(() => {
    return commissionPlanListResponse?.referralCommissionPlans?.edges.map(edge => edge.node) || [];
  }, [commissionPlanListResponse]);

  const currentCommission = useMemo(() => {
    return commissionPlanList.find(plan => plan.id === selectedCommissionId);
  }, [commissionPlanList, selectedCommissionId]);

  const tierCalculationType = currentCommission?.tiers[0]?.calculationRules[0]?.calculationType;

  return (
    <>
      <div className="partner-commission-plan-editor__body">
        <section className="partner-commission-plan-editor__section">
          {commissionPlanListLoading && !commissionPlanList.length ? (
            <div className="partner-commission-plan-editor__spin">
              <Spin />
            </div>
          ) : (
            <main className="partner-commission-plan-editor__main">
              <div className="">
                <h3 className="partner-commission-plan-editor__selector-title">
                  {t('cms.referral.partner.detail.editor.commissionPlan.selector')}
                </h3>
                <Select
                  loading={commissionPlanListLoading}
                  className="partner-commission-plan-editor__selector"
                  showSearch
                  value={selectedCommissionId}
                  placeholder={'Please Select Commission Plan'}
                  optionFilterProp="children"
                  // defaultActiveFirstOption={false}
                  // showArrow={false}
                  // filterOption={false}
                  // onSearch={this.handleSearch}
                  onChange={value => {
                    setSelectCommissionId(value);
                  }}
                >
                  {commissionPlanList.map(plan => (
                    <Select.Option key={plan.id}>{plan.name}</Select.Option>
                  ))}
                </Select>
              </div>

              <If condition={currentCommission}>
                <div className="partner-commission-plan-editor__miniTable">
                  <div className="partner-commission-plan-editor__miniTable__table-header">
                    <div className="partner-commission-plan-editor__miniTable--w-40">
                      {t('cms.referral.partner.detail.tab.commission.table.header.bookingsNo')}
                    </div>
                    <div className="partner-commission-plan-editor__miniTable--w-30 partner-commission-plan-editor__miniTable--text-right">
                      {t('cms.referral.partner.detail.tab.commission.table.header.longTerm', {
                        currency:
                          tierCalculationType === 'PERCENTAGE' ? '%' : currentCommission.currency,
                      })}
                    </div>
                    <div className="partner-commission-plan-editor__miniTable--w-30 partner-commission-plan-editor__miniTable--text-right">
                      {t('cms.referral.partner.detail.tab.commission.table.header.shortTerm', {
                        currency:
                          tierCalculationType === 'PERCENTAGE' ? '%' : currentCommission.currency,
                      })}
                    </div>
                  </div>
                  <div className="partner-commission-plan-editor__miniTable__divider" />
                  {currentCommission.tiers.map((tier, tierIndex) => (
                    <div
                      key={tier.name}
                      className={classNames('partner-commission-plan-editor__miniTable__row')}
                    >
                      <div className="partner-commission-plan-editor__miniTable__column-body partner-commission-plan-editor__miniTable--w-40 partner-commission-plan-editor__miniTable--flex">
                        {tierIndex === currentCommission.tiers.length - 1 &&
                        tier.bookingCountTo === null
                          ? `≥ ${tier.bookingCountFrom}`
                          : `${tier.bookingCountFrom} - ${tier.bookingCountTo}`}
                        <Tag
                          title={tier.name}
                          className="partner-commission-plan-editor__miniTable__tag"
                        >
                          {tier.name}
                        </Tag>
                      </div>
                      <div className="partner-commission-plan-editor__miniTable__column-body partner-commission-plan-editor__miniTable--w-30 partner-commission-plan-editor__miniTable--text-right">
                        <span>{formatPriceWithNoSymbol(tier.longTermCalculationValue)}</span>
                      </div>
                      <div className="partner-commission-plan-editor__miniTable__column-body partner-commission-plan-editor__miniTable--w-30 partner-commission-plan-editor__miniTable--text-right">
                        <span>{formatPriceWithNoSymbol(tier.shortTermCalculationValue)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </If>
            </main>
          )}
        </section>
      </div>
      <div className="partner-commission-plan-editor__footer">
        <Button
          size="large"
          className="partner-commission-plan-editor__cancel-button"
          onClick={onClose}
        >
          {t('cms.referral.partner.detail.editor.btn.cancel')}
        </Button>
        <Button
          type="primary"
          size="large"
          className="partner-commission-plan-editor__save-button"
          loading={updatePartnerCommissionPlanLoading}
          onClick={() => handleSubmit(partnerId, selectedCommissionId)}
        >
          {t('cms.referral.partner.detail.editor.btn.save')}
        </Button>
      </div>
    </>
  );
}

export default PartnerDetailCommissionPlanEditor;
